import { EParetoActionType, IParetoInput } from 'store/pareto/types';
import { EStorageField } from 'lib/storage';
import { IPortfolioWithDetails, IPortfolioDeal } from 'store/portfolio/types';
import { IStoreState } from 'store';
import { setLoading } from 'store/loading/actions';
import { ELoadingType } from 'store/loading/types';
import apiService from 'services/api';
import { getAccessToken } from 'lib/auth';
import apiRoutes from 'services/api/routes';
import { getAxiosErrorMessage, handleError } from 'lib/helpers/error-handler';
import { setParetoIsModalOpen } from 'store/pareto/actions';
import { IDeal } from 'store/deals/types';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';

export const setPortfolioDeals = (portfolioDeals: IPortfolioWithDetails[]) => (
  dispatch: ThunkDispatch<void, void, AnyAction>
) => {
  localStorage.setItem(EStorageField.PORTFOLIO_DEALS, JSON.stringify(portfolioDeals));
  dispatch({
    type: EParetoActionType.SET_PORTFOLIO_DEALS,
    payload: portfolioDeals,
  });
};

const checkDealStatus = (deal: { status: string }, status: { name: string }) => {
  return deal.status === status.name;
};

const getDealDataId = (dealData: IDeal) => {
  return dealData.id ?? '';
};

const getPortfolioSharpeRatio = (deal: IPortfolioDeal) => {
  const sharpRatioHigh = 99999.99;
  return deal.sharpeRatio === sharpRatioHigh ? 0 : deal.sharpeRatio;
};

const comparePortfoliosValue = (b: IPortfolioWithDetails, a: IPortfolioWithDetails) => {
  return b.value === a.value;
};

const evaluateReturnValue = (b: IPortfolioWithDetails, a: IPortfolioWithDetails) => {
  return (a.value || 0) - (b.value || 0);
};

const evaluateDefaultReturnValue = (b: IPortfolioWithDetails, a: IPortfolioWithDetails) => {
  return (b.value || 0) - (a.value || 0);
};

const checkDealDataLengh = (dealDataArray: IDeal[]) => {
  return !dealDataArray.length;
};

const getPortfoliosData = (portfolios: IPortfolioWithDetails[]) => {
  return portfolios.sort((a, b) => {
    if (comparePortfoliosValue(b, a)) {
      return evaluateReturnValue(b, a);
    }
    return evaluateDefaultReturnValue(b, a);
  });
};

export const evaluatePortfolioDeals = () => {
  return (dispatch: ThunkDispatch<void, void, AnyAction>, getState: () => IStoreState) => {
    dispatch(setLoading(ELoadingType.SPINNER));

    const { pareto, dealEvaluation, fieldOptions, deals } = getState();
    const { portfolioDealIds } = pareto;
    const { selectedUnitType, selectedForecastType, selectedCostFormulaType } = dealEvaluation;
    const { statuses } = fieldOptions;
    const { data } = deals;

    const postBody: IParetoInput = {
      dealIDs: portfolioDealIds,
    };

    apiService
      .post(apiRoutes.portfolioEvaluation, postBody, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
        params: {
          forecast: selectedForecastType,
          costType: selectedCostFormulaType,
          outputUnit: selectedUnitType,
        },
      })
      .then(response => {
        const { errors, result } = response.data;
        if (errors.length) {
          handleError(errors[0]);
          return;
        }
        const portfolios: IPortfolioWithDetails[] = [];
        result.forEach((deal: IPortfolioDeal) => {
          // map deal data to paretoDeal evaluation
          const dealDataArray = data.filter(d => d.name === deal.name);
          if (checkDealDataLengh(dealDataArray)) {
            return;
          }
          const dealData = dealDataArray[0];
          let color = 'grey';
          if (statuses) {
            statuses.forEach(status => {
              if (checkDealStatus(deal, status)) {
                color = status.color;
              }
            });
          }
          portfolios.push({
            ...deal,
            dealId: getDealDataId(dealData),
            color,
            value: getPortfolioSharpeRatio(deal),
          });
        });

        dispatch(setPortfolioDeals(getPortfoliosData(portfolios)));
        dispatch(setParetoIsModalOpen(false, pareto.modalTitle));
      })
      .catch(error => {
        const errorMessage = getAxiosErrorMessage(error);
        handleError(errorMessage);
      })
      .finally(() => dispatch(setLoading(ELoadingType.NONE)));
  };
};

export const setPortfolioDealIds = (portfolioDealIds: string[]) => (
  dispatch: ThunkDispatch<IStoreState, void, AnyAction>
) => {
  localStorage.setItem(EStorageField.PORTFOLIO_DEAL_IDS, JSON.stringify(portfolioDealIds));
  dispatch({
    type: EParetoActionType.SET_PORTFOLIO_DEAL_IDS,
    payload: portfolioDealIds,
  });
  dispatch(evaluatePortfolioDeals());
};

export const removePortfolioDealAndId = (portfolioDealName: string, portfolioDealId: string) => {
  return (dispatch: ThunkDispatch<void, void, AnyAction>, getState: () => IStoreState) => {
    const { pareto } = getState();
    const { portfolioDeals, portfolioDealIds } = pareto;
    const portfolioDealsUpdated: IPortfolioDeal[] = portfolioDeals.filter(
      portfolioDeal => portfolioDeal.name !== portfolioDealName
    );
    const portfolioDealIdsUpdated: string[] = portfolioDealIds.filter(pid => pid !== portfolioDealId);
    localStorage.setItem(EStorageField.PORTFOLIO_DEALS, JSON.stringify(portfolioDealsUpdated));
    localStorage.setItem(EStorageField.PORTFOLIO_DEAL_IDS, JSON.stringify(portfolioDealIdsUpdated));

    dispatch({
      type: EParetoActionType.SET_PORTFOLIO_DEALS,
      payload: portfolioDealsUpdated,
    });
    dispatch({
      type: EParetoActionType.SET_PORTFOLIO_DEAL_IDS,
      payload: portfolioDealIdsUpdated,
    });
  };
};
